import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { Loader2 } from "lucide-react";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireEmployee?: boolean;
  requireAdmin?: boolean;
}

export function ProtectedRoute({
  children,
  requireEmployee = false,
  requireAdmin = false,
}: ProtectedRouteProps) {
  const { user, isLoading, isEmployee, role } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <div className="text-center">
            <p className="text-sm font-medium">Checking session</p>
            <p className="text-xs text-muted-foreground">
              Restoring your workspace access.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  if (requireAdmin && role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold mb-2">Access Restricted</h1>
          <p className="text-muted-foreground">
            This section is only accessible to administrators.
          </p>
        </div>
      </div>
    );
  }

  if (requireEmployee && !isEmployee) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center max-w-md">
          <h1 className="text-xl font-semibold mb-2">Access Restricted</h1>
          <p className="text-muted-foreground">
            This section is only accessible to employees. Please contact an
            administrator if you need access.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
